import { useCallback } from "react";
import { useSelector } from "react-redux";
import { Typography } from "@mui/material";

import makeStyles from "@mui/styles/makeStyles";

import { FULL_DAYS } from "../../../consts/strings";

import { selectedDriverDaySelector } from "../../../reducers/ownApp/selectors";
import {
  sectionDaySelector,
  matrixDayRoutesSelector,
  driverDayEditSelector,
  vehiclesSelector,
  matrixDayDriversSelector,
} from "../../../reducers/driverManagement/selectors";

import { palette } from "../../../theme/palette";
import { WorkScheduleDayFormMemo } from "../../WorkScheduleDayForm";
import { View } from "../../View";

const useStyles = makeStyles({
  columnHeader: {
    color: palette.darkGrey,
    fontSize: "1rem",
    fontWeight: "500",
    backgroundColor: palette.white,
    marginTop: "0.25rem",
  },
});

const useFormStyles = makeStyles({
  // Form Grid
  formContainer: {
    display: "grid",
    margin: "0.5rem 0 0 0.7rem",
    gap: "0.7rem",
  },

  // Common controls
  commonControlContainer: {
    scale: 0.95,
    minHeight: "2.5rem",
  },

  // 1st Row
  odfDropDownContainer: {
    gridColumnStart: 1,
    gridColumnEnd: 3,
    gridRowStart: 1,
  },
  odfDropDown: {
    width: "12.5rem",
  },

  // 2nd Row
  driverDropDownContainer: {
    gridColumnStart: 1,
    gridColumnEnd: 3,
    gridRowStart: 2,
  },
  driverDropDown: {
    width: "12.5rem",
  },

  // 3rd Row
  routeCodeContainer: {
    gridColumnStart: 1,
    gridColumnEnd: 3,
    gridRowStart: 3,
  },

  // 4rd Row
  dutyStartTimeContainer: {
    gridColumnStart: 1,
    gridColumnEnd: 3,
    gridRowStart: 4,
    width: "12.5rem",
  },

  // 5th Row
  dutyEndTimeContainer: {
    gridColumnStart: 1,
    gridColumnEnd: 3,
    gridRowStart: 5,
    width: "12.5rem",
  },

  // 6th Row
  totalDutyTimeContainer: {
    gridColumnStart: 1,
    gridColumnEnd: 3,
    gridRowStart: 6,
    width: "12.5rem",
  },

  // 7th Row
  plannedDepartTimeContainer: {
    gridColumnStart: 1,
    gridColumnEnd: 3,
    gridRowStart: 7,
    width: "12.5rem",
  },

  // 8th Row
  plannedReturnTimeContainer: {
    gridColumnStart: 1,
    gridColumnEnd: 3,
    gridRowStart: 8,
    width: "12.5rem",
  },

  // 9th Row
  amTimeInDepotContainer: {
    gridColumnStart: 1,
    gridColumnEnd: 3,
    gridRowStart: 9,
    width: "12.5rem",
  },

  // 10th Row
  pmTimeInDepotContainer: {
    gridColumnStart: 1,
    gridColumnEnd: 3,
    gridRowStart: 10,
    width: "12.5rem",
  },

  // 11th Row
  deliveryStopTimeContainer: {
    gridColumnStart: 1,
    gridColumnEnd: 3,
    gridRowStart: 11,
    width: "12.5rem",
  },

  // 12th Row
  collectionStopTimeContainer: {
    gridColumnStart: 1,
    gridColumnEnd: 3,
    gridRowStart: 12,
    width: "12.5rem",
  },

  // 13th Row
  sectionDropDownContainer: {
    gridColumnStart: 1,
    gridColumnEnd: 3,
    gridRowStart: 13,
  },
  sectionDropDown: {
    width: "12.5rem",
  },

  // 14th Row
  leaveFromCheckboxContainer: {
    gridColumnStart: 1,
    gridColumnEnd: 3,
    gridRowStart: 14,
    scale: 1.1,
    marginLeft: "0.75rem",
    marginTop: "-0.5rem",
  },
  // 15th row
  returnToCheckboxContainer: {
    gridColumnStart: 1,
    gridColumnEnd: 3,
    gridRowStart: 15,
    scale: 1.1,
    marginLeft: "0.75rem",
    marginTop: "-1.5rem",
  },
});

export const WorkScheduleModalContent = ({
  disabledControls = {},
  hiddenControls = {},
  dayOfWeek,
  onChange: onDayChange,
  onValidation: onDayValidation,
}) => {
  const styles = useStyles();
  const formStyles = useFormStyles();
  const vehicles = useSelector(vehiclesSelector);

  const sections = useSelector(sectionDaySelector(dayOfWeek));
  const drivers = useSelector(matrixDayDriversSelector(dayOfWeek));
  const routes = useSelector(matrixDayRoutesSelector(dayOfWeek));
  const driverDayEdit = useSelector(driverDayEditSelector(dayOfWeek));
  const selectedDriverDay = useSelector(selectedDriverDaySelector(dayOfWeek));

  const onChange = useCallback(
    ({ name, value }) => onDayChange({ name, value, dayOfWeek }),
    [dayOfWeek, onDayChange]
  );
  const onValidation = useCallback(
    (hasErrors) => onDayValidation({ hasErrors, dayOfWeek }),
    [dayOfWeek, onDayValidation]
  );

  return (
    <View>
      <Typography align="center" className={styles.columnHeader}>
        {FULL_DAYS[dayOfWeek]}
      </Typography>
      <WorkScheduleDayFormMemo
        styles={formStyles}
        onChange={onChange}
        onValidation={onValidation}
        sections={sections}
        vehicles={vehicles}
        routes={routes}
        drivers={drivers}
        driverEdit={driverDayEdit || selectedDriverDay}
        viewOnly={!driverDayEdit}
        disabledControls={disabledControls}
        hiddenControls={hiddenControls}
        hideCheckboxInfoIcon={true}
      />
    </View>
  );
};
